import { el, clear } from '../../../core/dom.js';
import type { RescueModule, RescueResult } from '../../rescue-types.js';
import { minigameState } from '../../../storage/db.js';
import './air-hockey.css';

// Air Hockey — designed as a fast craving-distraction toy, not a fair sim.
// The CPU is intentionally flawed and human-like: stale perception, decision
// commitment, mistakes, panic, rubber-banding. Touch control uses an offset
// target so the player's finger never covers the mallet. Match length is short
// and rewards fire on saves/banks/slapshots, not just goals.

const MODULE_ID = 'air-hockey';

type AhMode = 'chill' | 'rival' | 'spicy';
const MODES: AhMode[] = ['chill', 'rival', 'spicy'];
const MODE_LABELS: Record<AhMode, string> = {
  chill: 'Chill',
  rival: 'Rival',
  spicy: 'Spicy',
};

interface Persisted {
  matchesWon: number;
  matchesLost: number;
  goalsFor: number;
  goalsAgainst: number;
  currentMatchStreak: number;
  longestMatchStreak: number;
  lastMode: AhMode;
}

function defaults(): Persisted {
  return {
    matchesWon: 0, matchesLost: 0,
    goalsFor: 0, goalsAgainst: 0,
    currentMatchStreak: 0, longestMatchStreak: 0,
    lastMode: 'rival',
  };
}

interface Vec { x: number; y: number }
interface Ball { p: Vec; v: Vec; r: number }
interface Paddle {
  p: Vec; v: Vec; r: number;
  prev: Vec;
}

interface AiProfile {
  label: AhMode;
  maxSpeed: number;
  maxAccel: number;
  reactionMs: number;
  decisionMs: number;
  aimErrorPx: number;
  predictionBounces: number;
  attackChance: number;
  hardShotChance: number;
  mistakeChance: number;
  whiffChance: number;
  overcommitChance: number;
  recoveryMs: number;
  defenseSafetyChance: number;
}

interface BallSnapshot { t: number; p: Vec; v: Vec }

interface Particle {
  p: Vec; v: Vec;
  life: number; maxLife: number;
  size: number;
  color: string;
  kind: 'spark' | 'trail' | 'goal';
}

interface DebugStats {
  matchMs: number;
  firstPlayerGoalMs: number | null;
  playerHits: number;
  cpuHits: number;
  playerGoals: number;
  cpuGoals: number;
  cpuWhiffs: number;
  greatSaves: number;
  maxRally: number;
}

const WIN_SCORE = 5;

function levelForMode(mode: AhMode): number {
  if (mode === 'chill') return 2;
  if (mode === 'rival') return 5;
  return 8;
}

function profileForMode(mode: AhMode): AiProfile {
  const lvl = levelForMode(mode);
  const t0 = Math.max(0, Math.min(1, (lvl - 1) / 9));
  // Profiles tuned per the design plan. Low difficulty isn't "slow perfect
  // goalie" — it's a flawed, lively opponent that creates punishable mistakes.
  if (mode === 'chill') {
    return {
      label: 'chill',
      maxSpeed: 480,
      maxAccel: 1600,
      reactionMs: 460,
      decisionMs: 260,
      aimErrorPx: 22,
      predictionBounces: 0,
      attackChance: 0.62,
      hardShotChance: 0.10,
      mistakeChance: 0.28,
      whiffChance: 0.12,
      overcommitChance: 0.22,
      recoveryMs: 260,
      defenseSafetyChance: 0.40,
    };
  }
  if (mode === 'rival') {
    return {
      label: 'rival',
      maxSpeed: 880,
      maxAccel: 3200,
      reactionMs: 220,
      decisionMs: 200,
      aimErrorPx: 10,
      predictionBounces: 1,
      attackChance: 0.78,
      hardShotChance: 0.45,
      mistakeChance: 0.07,
      whiffChance: 0.03,
      overcommitChance: 0.08,
      recoveryMs: 130,
      defenseSafetyChance: 0.75,
    };
  }
  // Spicy — fast, accurate, rarely panics. Should win most matches.
  return {
    label: 'spicy',
    maxSpeed: 1700,
    maxAccel: 9000,
    reactionMs: 70,
    decisionMs: 110,
    aimErrorPx: 2,
    predictionBounces: 4,
    attackChance: 0.85,
    hardShotChance: 0.70,
    mistakeChance: 0.015,
    whiffChance: 0.003,
    overcommitChance: 0.02,
    recoveryMs: 60,
    defenseSafetyChance: 0.99,
  };
  void t0;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function haptic(kind: 'tap' | 'save' | 'goal' | 'lose'): void {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  if (prefersReducedMotion()) return;
  const pattern =
    kind === 'goal' ? [20, 30, 35] :
    kind === 'save' ? [12] :
    kind === 'lose' ? [10] :
    [6];
  navigator.vibrate(pattern);
}

function rand(lo: number, hi: number): number { return lo + Math.random() * (hi - lo); }
function clampNum(n: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, n)); }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function clampMag(v: number, m: number): number { return Math.max(-m, Math.min(m, v)); }

export const airHockey: RescueModule = {
  id: MODULE_ID,
  name: 'Air Hockey',
  kind: 'minigame',
  defaultDurationSec: 150,
  reducedMotion: false,
  safety: {
    gamblingLike: false,
    excludeForDomains: [],
    experimental: false,
    reducedMotionSafe: true,
  },
  async run(host) {
    return new Promise<RescueResult>((resolve) => { void mount(host, resolve); });
  },
};

async function mount(host: HTMLElement, resolve: (r: RescueResult) => void): Promise<void> {
  clear(host);
  document.body.classList.add('ah-active');
  const raw = (await minigameState.get<Partial<Persisted> & { lastDifficulty?: number | string }>(MODULE_ID)) ?? {};
  const persisted: Persisted = { ...defaults(), ...raw } as Persisted;
  // Migrate legacy lastDifficulty (1..10 or 'easy/normal/hard') into a mode.
  if (!raw.lastMode && raw.lastDifficulty !== undefined) {
    const v = raw.lastDifficulty;
    if (typeof v === 'string') {
      persisted.lastMode = v === 'easy' ? 'chill' : v === 'hard' ? 'spicy' : 'rival';
    } else {
      const n = Number(v);
      persisted.lastMode = n <= 3 ? 'chill' : n >= 8 ? 'spicy' : 'rival';
    }
  }
  let mode: AhMode = persisted.lastMode;

  // ---- DOM ----
  const stage = el('div', { class: 'ah-stage' });

  const youNum = el('div', { class: 'num', dataset: { side: 'you' } }, '0');
  const cpuNum = el('div', { class: 'num', dataset: { side: 'cpu' } }, '0');
  const scoreEl = el('div', { class: 'ah-score' },
    el('div', { class: 'you' }, el('div', { class: 'lbl' }, 'You'), youNum),
    el('span', { class: 'sep' }, ':'),
    el('div', { class: 'cpu' }, el('div', { class: 'lbl' }, 'CPU'), cpuNum),
  );

  const modePills = el('div', { class: 'ah-mode-pills', attrs: { role: 'tablist', 'aria-label': 'Difficulty' } });
  const modeBtns: Record<AhMode, HTMLButtonElement> = {} as Record<AhMode, HTMLButtonElement>;
  for (const m of MODES) {
    const btn = el('button', {
      class: 'ah-mode-pill' + (m === mode ? ' active' : ''),
      attrs: { role: 'tab', 'aria-selected': m === mode ? 'true' : 'false' },
      onclick: () => setMode(m),
    }, MODE_LABELS[m]) as HTMLButtonElement;
    modeBtns[m] = btn;
    modePills.appendChild(btn);
  }

  const closeBtn = el('button', {
    class: 'ah-close-x', attrs: { 'aria-label': 'Close air hockey' },
    onclick: () => doClose(),
  }, '×');

  const topbar = el('div', { class: 'ah-topbar' }, scoreEl, modePills, closeBtn);
  const rallyLine = el('div', { class: 'ah-rally', dataset: { role: 'rally' } }, '');

  const wrap = el('div', { class: 'ah-canvas-wrap' });
  const canvas = document.createElement('canvas');
  wrap.appendChild(canvas);
  const popLayer = el('div', { class: 'ah-pop-layer' });
  wrap.appendChild(popLayer);

  stage.appendChild(topbar);
  stage.appendChild(rallyLine);
  stage.appendChild(wrap);
  host.appendChild(stage);

  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) {
    document.body.classList.remove('ah-active');
    resolve({ kind: 'abandoned', details: { reason: 'no-canvas' } });
    return;
  }

  // ---- Field ----
  let W = 0, H = 0;
  let dpr = 1;

  // ---- Entities ----
  let ball: Ball = { p: { x: 0, y: 0 }, v: { x: 0, y: 0 }, r: 12 };
  const player: Paddle = { p: { x: 0, y: 0 }, v: { x: 0, y: 0 }, r: 28, prev: { x: 0, y: 0 } };
  const cpu: Paddle = { p: { x: 0, y: 0 }, v: { x: 0, y: 0 }, r: 28, prev: { x: 0, y: 0 } };

  // ---- Player control (target-based, finger offset) ----
  let playerTarget: Vec | null = null;
  let fingerPos: Vec | null = null;
  let fingerSeenMs = 0;
  let dragging = false;

  // ---- CPU state ----
  let cpuRecoverMs = 0;
  let cpuFrozenMs = 0;
  const smoothCpuTarget: Vec = { x: 0, y: 0 };
  let smoothInit = false;
  const ballHistory: BallSnapshot[] = [];

  // ---- Match state ----
  let scoreYou = 0, scoreCpu = 0;
  let phase: 'serve' | 'play' | 'goal' | 'ended' = 'serve';
  let serveTimer = 0;
  let pendingServeTo: 'you' | 'cpu' = 'you';
  let nowMs = 0;
  let lastFrame = performance.now();
  let rafId = 0;
  let alive = true;
  let goalFlash: { side: 'you' | 'cpu'; t: number } | null = null;
  let endNode: HTMLElement | null = null;
  // Hit tracking
  let lastTouch: 'you' | 'cpu' | 'wall' | null = null;
  let wallBouncesSinceYouHit = 0;
  let rallyHits = 0;
  let nextRallyMilestone = 5;
  let rallyMs = 0;
  // Rally only increments when the puck crosses the halfway line between
  // paddle hits. Stops rally inflation from rapid same-side re-contacts
  // (slow pushes, edge clipping) where the puck never reaches the opponent.
  let puckCrossedHalfwaySinceLastHit = true;
  let cpuGoalThreatMs = 0;
  let dangerZoneActive = false;

  // Deadlock detector — puck barely moving for too long triggers a kick.
  let puckIdleMs = 0;

  // ---- Particles ----
  const particles: Particle[] = [];
  const trailEvery = 0.022; // sec
  let trailAcc = 0;

  // ---- Debug stats (visible on end screen) ----
  const dbg: DebugStats = {
    matchMs: 0, firstPlayerGoalMs: null,
    playerHits: 0, cpuHits: 0,
    playerGoals: 0, cpuGoals: 0,
    cpuWhiffs: 0, greatSaves: 0,
    maxRally: 0,
  };

  // ---- Cleanup ----
  const cleanupFns: Array<() => void> = [];
  function addCleanup(fn: () => void): void { cleanupFns.push(fn); }
  const pendingTimeouts = new Set<number>();
  function later(fn: () => void, ms: number): number {
    const id = window.setTimeout(() => { pendingTimeouts.delete(id); fn(); }, ms);
    pendingTimeouts.add(id);
    return id;
  }

  function setMode(m: AhMode): void {
    if (m === mode) return;
    mode = m;
    persisted.lastMode = m;
    void minigameState.put(MODULE_ID, persisted);
    for (const k of MODES) {
      const b = modeBtns[k];
      b.classList.toggle('active', k === m);
      b.setAttribute('aria-selected', k === m ? 'true' : 'false');
    }
  }

  function resize(): void {
    const rect = wrap.getBoundingClientRect();
    // Reserve vertical headroom on both ends so the goal pockets fit visibly
    // outside the playfield. World Y range is [0, H]; canvas Y is shifted
    // down by GOAL_DEPTH so y=0 sits below the top pocket.
    const margin = GOAL_DEPTH * 2;
    const maxW = Math.min(rect.width, 600);
    const maxH = Math.min(rect.height - margin, 1000);
    let cw = maxW, ch = maxW * 1.6;
    if (ch > maxH) { ch = maxH; cw = ch / 1.6; }
    W = Math.floor(cw); H = Math.floor(ch);
    dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    const cssW = W;
    const cssH = H + GOAL_DEPTH * 2;
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    ctx2d!.setTransform(dpr, 0, 0, dpr, 0, GOAL_DEPTH * dpr);

    if (phase === 'serve') {
      ball.p = { x: W / 2, y: H / 2 };
      ball.v = { x: 0, y: 0 };
    }
    player.p = clampPos(player.p, true);
    cpu.p = clampPos(cpu.p, false);
    const r = Math.max(22, Math.round(W * 0.072));
    player.r = cpu.r = r;
    ball.r = Math.round(r * 0.7);
  }

  function clampPos(p: Vec, isPlayer: boolean): Vec {
    const r = isPlayer ? player.r : cpu.r;
    const minY = isPlayer ? H / 2 + r + 2 : r + 2;
    const maxY = isPlayer ? H - r - 2 : H / 2 - r - 2;
    return {
      x: clampNum(p.x, r + 2, W - r - 2),
      y: clampNum(p.y, minY, maxY),
    };
  }

  function reset(serveTo: 'you' | 'cpu' = Math.random() < 0.5 ? 'you' : 'cpu'): void {
    ball.p = { x: W / 2, y: H / 2 };
    ball.v = { x: 0, y: 0 };
    player.p = { x: W / 2, y: H * 0.78 };
    player.v = { x: 0, y: 0 };
    player.prev = { ...player.p };
    cpu.p = { x: W / 2, y: H * 0.22 };
    cpu.v = { x: 0, y: 0 };
    cpu.prev = { ...cpu.p };
    cpuRecoverMs = 0;
    cpuFrozenMs = 0;
    smoothInit = false;
    phase = 'serve';
    serveTimer = 360;
    pendingServeTo = serveTo;
    lastTouch = null;
    rallyHits = 0;
    nextRallyMilestone = 5;
    rallyMs = 0;
    puckIdleMs = 0;
    puckCrossedHalfwaySinceLastHit = true;
    wallBouncesSinceYouHit = 0;
    ballHistory.length = 0;
    setText('[data-role="rally"]', '');
  }

  function startServe(): void {
    const dir = pendingServeTo === 'you' ? 1 : -1;
    const speed = rand(330, 430);
    const angle = rand(-0.45, 0.45);
    ball.v = {
      x: Math.sin(angle) * speed,
      y: Math.cos(angle) * speed * dir,
    };
    phase = 'play';
  }

  // ---- Input ----
  function pointerToCanvas(e: PointerEvent): Vec {
    const rect = canvas.getBoundingClientRect();
    // Convert client coords into world-space. Canvas is taller than the
    // playfield by GOAL_DEPTH on each end, so subtract that to get y=0 at
    // the top edge of the field.
    return { x: e.clientX - rect.left, y: e.clientY - rect.top - GOAL_DEPTH };
  }
  function setPlayerTarget(raw: Vec, isTouch: boolean): void {
    fingerPos = { ...raw };
    fingerSeenMs = nowMs;
    // Offset only for touch — finger covers the paddle. Mouse has no occlusion
    // so paddle sits exactly under the cursor.
    const offsetY = isTouch ? clampNum(H * 0.035, 16, 32) : 0;
    playerTarget = clampPos({ x: raw.x, y: raw.y - offsetY }, true);
  }
  const onPointerDown = (e: PointerEvent): void => {
    canvas.setPointerCapture(e.pointerId);
    dragging = true;
    setPlayerTarget(pointerToCanvas(e), e.pointerType === 'touch');
    e.preventDefault();
  };
  const onPointerMove = (e: PointerEvent): void => {
    // Mouse: follow cursor any time it's over the canvas (no click required).
    // Touch/pen: require drag, otherwise we'd never know when contact starts.
    if (e.pointerType === 'mouse' || dragging) {
      setPlayerTarget(pointerToCanvas(e), e.pointerType === 'touch');
      e.preventDefault();
    }
  };
  const onPointerUp = (): void => { dragging = false; };
  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  addCleanup(() => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerUp);
  });

  // ---- Ball history / perception ----
  function rememberBall(): void {
    ballHistory.push({ t: nowMs, p: { ...ball.p }, v: { ...ball.v } });
    while (ballHistory.length > 0 && nowMs - (ballHistory[0]!.t) > 1400) {
      ballHistory.shift();
    }
  }
  function sampleBall(reactionMs: number): BallSnapshot {
    const targetT = nowMs - reactionMs;
    for (let i = ballHistory.length - 1; i >= 0; i--) {
      const s = ballHistory[i]!;
      if (s.t <= targetT) return s;
    }
    return ballHistory[0] ?? { t: nowMs, p: { ...ball.p }, v: { ...ball.v } };
  }
  function getNoisySeenBall(profile: AiProfile): BallSnapshot {
    const seen = sampleBall(profile.reactionMs);
    // Velocity noise scaled to aim error — small errors on spicy, large on
    // chill. ±20% was overkill and caused per-frame paddle jitter.
    const vNoise = 0.04 + profile.aimErrorPx * 0.012;
    return {
      t: seen.t,
      p: {
        x: seen.p.x + rand(-profile.aimErrorPx, profile.aimErrorPx),
        y: seen.p.y + rand(-profile.aimErrorPx * 0.4, profile.aimErrorPx * 0.4),
      },
      v: {
        x: seen.v.x * (1 + rand(-vNoise, vNoise)),
        y: seen.v.y * (1 + rand(-vNoise, vNoise)),
      },
    };
  }

  // ---- Rubber band ----
  function getEffectiveProfile(): AiProfile {
    const base = profileForMode(mode);
    const lead = scoreCpu - scoreYou;
    if (lead >= 2) {
      base.reactionMs += 160;
      base.attackChance *= 0.65;
      base.mistakeChance += 0.15;
      base.recoveryMs += 180;
    }
    if (scoreCpu >= 3 && scoreYou === 0) {
      base.whiffChance += 0.18;
    }
    return base;
  }

  // ---- CPU AI (rewritten) ----
  // Single per-frame target. No state machine, no decision ticks. Key rules:
  //  • Defend first: if puck speeding toward own goal, intercept at goalie line.
  //  • Otherwise attack: get above the puck (smaller y) and drive +y through it.
  //  • If puck is behind paddle (paddle.y > puck.y on CPU side), NEVER push
  //    Y toward goal first — go sideways until lateral clearance exists,
  //    then loop up past the puck. Fixes the "shoves it into own net" bug.
  //  • Aggressive flag drives accel time-constant in moveCpuToward.

  function predictPuckX(seen: BallSnapshot, targetY: number, profile: AiProfile): number {
    const dy = seen.p.y - targetY;
    const vy = seen.v.y;
    const time = vy < -1 ? dy / -vy : vy > 1 ? -dy / vy : 0;
    let x = seen.p.x + seen.v.x * Math.max(0, time);
    for (let i = 0; i < profile.predictionBounces; i++) {
      if (x < ball.r) x = -x + 2 * ball.r;
      else if (x > W - ball.r) x = 2 * (W - ball.r) - x;
      else break;
    }
    return x;
  }

  interface CpuTarget { x: number; y: number; aggressive: boolean }

  function computeCpuTarget(seen: BallSnapshot, profile: AiProfile): CpuTarget {
    const homeY = cpu.r + 12;
    const halfY = H / 2 - cpu.r - 4;
    const goalieFactor = profile.label === 'chill' ? 0.16 : profile.label === 'rival' ? 0.10 : 0.06;
    const goalieY = clampNum(H * goalieFactor, homeY, halfY);
    const aimErr = profile.aimErrorPx;

    const puckOnCpuSide = seen.p.y < H / 2;
    const incoming = seen.v.y < -160;
    const fastIncoming = seen.v.y < -260;

    // 1) Fast incoming shot — intercept at goalie line.
    if (fastIncoming) {
      const x = predictPuckX(seen, goalieY, profile) + rand(-aimErr, aimErr);
      return { x: clampNum(x, cpu.r + 2, W - cpu.r - 2), y: goalieY, aggressive: true };
    }

    // 2) Puck on player side and not coming back fast — go home, mirror X.
    if (!puckOnCpuSide && seen.v.y > -40) {
      const x = lerp(W / 2, seen.p.x, 0.35) + rand(-aimErr * 0.5, aimErr * 0.5);
      return { x: clampNum(x, cpu.r + 2, W - cpu.r - 2), y: goalieY, aggressive: false };
    }

    // 3) Puck behind paddle (closer to own goal than paddle) — CRITICAL:
    //    must not approach Y until lateral clearance exists.
    const puckBehind = cpu.p.y > seen.p.y - cpu.r * 0.3;
    if (puckBehind && (puckOnCpuSide || incoming)) {
      const lateralGap = Math.abs(cpu.p.x - seen.p.x);
      const safeGap = cpu.r + ball.r + 14;
      if (lateralGap < safeGap) {
        // Move sideways only. Pick whichever side paddle is already closer to.
        const sideSign = cpu.p.x <= seen.p.x ? -1 : 1;
        const flankX = clampNum(seen.p.x + sideSign * (safeGap + 6), cpu.r + 2, W - cpu.r - 2);
        return { x: flankX, y: cpu.p.y, aggressive: true };
      }
      // Have lateral clearance — loop up past puck Y to get behind it for strike.
      const aboveY = clampNum(seen.p.y - cpu.r - ball.r - 6, homeY, halfY);
      return { x: cpu.p.x, y: aboveY, aggressive: true };
    }

    // 4) Attack: pre-position at intercept, then drive THROUGH puck.
    //    a) approach — target predicted puck position at strike Y (not chase).
    //    b) strike  — when aligned + at strike Y, drive far past puck so
    //       moveCpuToward never decelerates near contact (= real follow-through).
    const strikeClearance = cpu.r + ball.r + 2;
    const idealStrikeY = clampNum(seen.p.y - strikeClearance, homeY, halfY);
    // Predict where puck will be when paddle Y arrives at idealStrikeY.
    // For incoming puck use intercept solver; for fleeing puck lead by time.
    let aimX: number;
    if (seen.v.y < -20) {
      aimX = predictPuckX(seen, idealStrikeY, profile);
    } else {
      const leadT = clampNum(Math.abs(cpu.p.y - idealStrikeY) / Math.max(120, profile.maxSpeed), 0.05, 0.25);
      aimX = seen.p.x + seen.v.x * leadT;
    }
    aimX += rand(-aimErr, aimErr);
    const alignedX = Math.abs(cpu.p.x - seen.p.x) < cpu.r * 0.8;
    const inStrikePos = Math.abs(cpu.p.y - idealStrikeY) < cpu.r * 0.7;
    if (alignedX && inStrikePos && cpuRecoverMs <= 0) {
      // Drive Y all the way to the half line. Paddle stays far from target
      // so desiredSpeed = dist / timeConst stays maxed = real slapshot force.
      return { x: clampNum(seen.p.x, cpu.r + 2, W - cpu.r - 2), y: halfY, aggressive: true };
    }
    return { x: clampNum(aimX, cpu.r + 2, W - cpu.r - 2), y: idealStrikeY, aggressive: true };
  }

  function moveCpuToward(tx: number, ty: number, dt: number, profile: AiProfile, aggressive: boolean): void {
    cpu.prev = { ...cpu.p };
    const dx = tx - cpu.p.x;
    const dy = ty - cpu.p.y;
    const dist = Math.hypot(dx, dy);
    let desiredVx = 0, desiredVy = 0;
    if (dist > 0.5) {
      const timeConst = aggressive ? 0.045 : 0.11;
      const desiredSpeed = Math.min(profile.maxSpeed, dist / timeConst);
      desiredVx = (dx / dist) * desiredSpeed;
      desiredVy = (dy / dist) * desiredSpeed;
    }
    const maxDv = profile.maxAccel * dt;
    cpu.v.x += clampMag(desiredVx - cpu.v.x, maxDv);
    cpu.v.y += clampMag(desiredVy - cpu.v.y, maxDv);
    if (dist < cpu.r * 0.4) {
      cpu.v.x *= Math.pow(0.001, dt);
      cpu.v.y *= Math.pow(0.001, dt);
    }
    const sp = Math.hypot(cpu.v.x, cpu.v.y);
    if (sp > profile.maxSpeed) {
      cpu.v.x *= profile.maxSpeed / sp;
      cpu.v.y *= profile.maxSpeed / sp;
    }
    cpu.p.x += cpu.v.x * dt;
    cpu.p.y += cpu.v.y * dt;
    const before = { ...cpu.p };
    cpu.p = clampPos(cpu.p, false);
    if (cpu.p.x !== before.x) cpu.v.x = 0;
    if (cpu.p.y !== before.y) cpu.v.y = 0;
  }

  function updateCpu(dt: number): void {
    const profile = getEffectiveProfile();
    const seen = getNoisySeenBall(profile);
    cpuRecoverMs = Math.max(0, cpuRecoverMs - dt * 1000);
    cpuFrozenMs = Math.max(0, cpuFrozenMs - dt * 1000);

    if (cpuFrozenMs > 0) {
      moveCpuToward(cpu.p.x, cpu.p.y, dt, profile, false);
      return;
    }

    // Sparse whiff — brief freeze. Only when puck isn't an immediate threat
    // so a "mistake" doesn't dump it into our net.
    const safeForMistake = !(seen.v.y < -200 && seen.p.y < H * 0.55);
    if (safeForMistake && Math.random() < profile.whiffChance * dt * 6) {
      cpuFrozenMs = rand(140, 320);
      dbg.cpuWhiffs += 1;
      return;
    }

    const tgt = computeCpuTarget(seen, profile);

    // Light smoothing only when not aggressive (calm hold/return). Aggressive
    // targets pass through raw for decisive commit.
    if (!smoothInit) { smoothCpuTarget.x = tgt.x; smoothCpuTarget.y = tgt.y; smoothInit = true; }
    let mx = tgt.x, my = tgt.y;
    if (!tgt.aggressive) {
      const a = Math.min(1, dt * 12);
      smoothCpuTarget.x = lerp(smoothCpuTarget.x, tgt.x, a);
      smoothCpuTarget.y = lerp(smoothCpuTarget.y, tgt.y, a);
      mx = smoothCpuTarget.x; my = smoothCpuTarget.y;
    } else {
      smoothCpuTarget.x = tgt.x;
      smoothCpuTarget.y = tgt.y;
    }
    moveCpuToward(mx, my, dt, profile, tgt.aggressive);
  }

  function updatePlayer(dt: number): void {
    player.prev = { ...player.p };
    if (playerTarget) {
      player.p.x = playerTarget.x;
      player.p.y = playerTarget.y;
      player.p = clampPos(player.p, true);
    }
    player.v.x = (player.p.x - player.prev.x) / Math.max(0.001, dt);
    player.v.y = (player.p.y - player.prev.y) / Math.max(0.001, dt);
    // Bleed velocity only when pointer is fully idle (no recent move and not
    // dragging). Mouse hover with no drag still produces real motion via snap.
    const idle = !dragging && nowMs - fingerSeenMs > 120;
    if (idle) {
      player.v.x *= Math.exp(-dt * 18);
      player.v.y *= Math.exp(-dt * 18);
    }
  }

  function updateBall(dt: number): void {
    if (phase === 'serve') {
      serveTimer -= dt * 1000;
      if (serveTimer <= 0) startServe();
      return;
    }
    if (phase !== 'play') return;

    // Friction for floating feel.
    const fr = Math.pow(0.993, dt * 60);
    ball.v.x *= fr; ball.v.y *= fr;

    let sp = Math.hypot(ball.v.x, ball.v.y);
    const maxSp = 1300;
    if (sp > maxSp) { ball.v.x *= maxSp / sp; ball.v.y *= maxSp / sp; sp = maxSp; }
    // Floor so the puck always glides without stalling.
    const minSp = 110;
    if (sp < minSp) {
      if (sp < 0.0001) {
        const ang = Math.random() * Math.PI * 2;
        ball.v.x = Math.cos(ang) * minSp; ball.v.y = Math.sin(ang) * minSp;
      } else {
        ball.v.x *= minSp / sp; ball.v.y *= minSp / sp;
      }
      sp = minSp;
    }

    rallyMs += dt * 1000;

    // Deadlock kick — if the puck has barely moved for >1.5s, it's wedged
    // somewhere (corner, against AI paddle holding it). Inject a random kick
    // toward field center so play resumes.
    if (sp < 140) puckIdleMs += dt * 1000;
    else puckIdleMs = 0;
    if (puckIdleMs > 1500) {
      const toCx = W / 2 - ball.p.x;
      const toCy = H / 2 - ball.p.y;
      const cLen = Math.hypot(toCx, toCy) || 1;
      const kick = 380;
      const jitter = rand(-0.5, 0.5);
      const baseX = toCx / cLen, baseY = toCy / cLen;
      const cs = Math.cos(jitter), sn = Math.sin(jitter);
      ball.v.x = (baseX * cs - baseY * sn) * kick;
      ball.v.y = (baseX * sn + baseY * cs) * kick;
      puckIdleMs = 0;
    }

    // Danger zone — puck approaching player goal with menace.
    cpuGoalThreatMs = Math.max(0, cpuGoalThreatMs - dt * 1000);
    const nowDanger = ball.v.y > 280 && ball.p.y > H * 0.55;
    dangerZoneActive = nowDanger;

    // Substep for stable collisions when fast.
    const steps = 1 + Math.floor(sp * dt / 9);
    const sdt = dt / steps;
    for (let s = 0; s < steps; s++) {
      const prevX = ball.p.x;
      const prevY = ball.p.y;
      ball.p.x += ball.v.x * sdt;
      ball.p.y += ball.v.y * sdt;
      if ((prevY <= H / 2 && ball.p.y > H / 2) || (prevY >= H / 2 && ball.p.y < H / 2)) {
        puckCrossedHalfwaySinceLastHit = true;
      }

      // Corner posts FIRST — swept against trajectory so fast diagonal pucks
      // can't tunnel into the goal past the post. Must run before wall clamps
      // would otherwise snap the ball off the original path.
      {
        const goalMidX0 = W / 2;
        const gLT = goalMidX0 - (W * goalWidthFor('you')) / 2;
        const gRT = goalMidX0 + (W * goalWidthFor('you')) / 2;
        const gLB = goalMidX0 - (W * goalWidthFor('cpu')) / 2;
        const gRB = goalMidX0 + (W * goalWidthFor('cpu')) / 2;
        collidePost({ x: gLT, y: 0 }, prevX, prevY);
        collidePost({ x: gRT, y: 0 }, prevX, prevY);
        collidePost({ x: gLB, y: H }, prevX, prevY);
        collidePost({ x: gRB, y: H }, prevX, prevY);
      }

      // Side walls — only when puck is inside the playfield Y range. Inside
      // the goal cavity, side wall logic is handled separately.
      if (ball.p.y > 0 && ball.p.y < H) {
        if (ball.p.x < ball.r) {
          ball.p.x = ball.r; ball.v.x = -ball.v.x * 0.96;
          onWallHit(ball.p.x, ball.p.y);
        }
        if (ball.p.x > W - ball.r) {
          ball.p.x = W - ball.r; ball.v.x = -ball.v.x * 0.96;
          onWallHit(ball.p.x, ball.p.y);
        }
      }

      const goalMidX = W / 2;
      const goalLeftT = goalMidX - (W * goalWidthFor('you')) / 2;
      const goalRightT = goalMidX + (W * goalWidthFor('you')) / 2;
      const goalLeftB = goalMidX - (W * goalWidthFor('cpu')) / 2;
      const goalRightB = goalMidX + (W * goalWidthFor('cpu')) / 2;

      // Top wall (CPU side, player scores here).
      if (ball.p.y < ball.r && ball.p.y >= 0) {
        const inMouth = ball.p.x > goalLeftT + POST_R && ball.p.x < goalRightT - POST_R;
        if (!inMouth) {
          ball.p.y = ball.r; ball.v.y = -ball.v.y * 0.96;
          onWallHit(ball.p.x, ball.p.y);
        }
      }
      // Inside top cavity — side walls of pocket + back wall (goal line).
      if (ball.p.y < 0) {
        if (ball.p.x - ball.r < goalLeftT + POST_R) {
          ball.p.x = goalLeftT + POST_R + ball.r;
          ball.v.x = Math.abs(ball.v.x) * 0.92;
          onWallHit(ball.p.x, ball.p.y);
        }
        if (ball.p.x + ball.r > goalRightT - POST_R) {
          ball.p.x = goalRightT - POST_R - ball.r;
          ball.v.x = -Math.abs(ball.v.x) * 0.92;
          onWallHit(ball.p.x, ball.p.y);
        }
        if (ball.p.y < -GOAL_DEPTH + ball.r) { scoreGoal('you'); return; }
      }

      // Bottom wall (player side, CPU scores here).
      if (ball.p.y > H - ball.r && ball.p.y <= H) {
        const inMouth = ball.p.x > goalLeftB + POST_R && ball.p.x < goalRightB - POST_R;
        if (!inMouth) {
          ball.p.y = H - ball.r; ball.v.y = -ball.v.y * 0.96;
          onWallHit(ball.p.x, ball.p.y);
        }
      }
      if (ball.p.y > H) {
        if (ball.p.x - ball.r < goalLeftB + POST_R) {
          ball.p.x = goalLeftB + POST_R + ball.r;
          ball.v.x = Math.abs(ball.v.x) * 0.92;
          onWallHit(ball.p.x, ball.p.y);
        }
        if (ball.p.x + ball.r > goalRightB - POST_R) {
          ball.p.x = goalRightB - POST_R - ball.r;
          ball.v.x = -Math.abs(ball.v.x) * 0.92;
          onWallHit(ball.p.x, ball.p.y);
        }
        if (ball.p.y > H + GOAL_DEPTH - ball.r) { scoreGoal('cpu'); return; }
      }

      collide(ball, player, 'you');
      collide(ball, cpu, 'cpu');
    }

    // Puck trail.
    if (!prefersReducedMotion()) {
      trailAcc += dt;
      while (trailAcc >= trailEvery) {
        trailAcc -= trailEvery;
        spawnTrail(ball.p.x, ball.p.y);
      }
    }

    // Close-call detection — puck nearly went into player goal but escaped
    // upward after touching player paddle in danger zone.
    if (lastTouch === 'you' && cpuGoalThreatMs > 0) {
      cpuGoalThreatMs = 0;
      pop('SAVE!', 'great');
      dbg.greatSaves += 1;
      haptic('save');
    }
  }

  function onWallHit(x: number, y: number): void {
    if (lastTouch === 'you') wallBouncesSinceYouHit += 1;
    if (!prefersReducedMotion()) spawnSparks(x, y, '#67e8f9', 5);
    lastTouch = 'wall';
  }

  function goalWidthFor(side: 'you' | 'cpu'): number {
    // side === 'you' is the goal the PLAYER scores into (top edge).
    if (mode === 'chill') return side === 'you' ? 0.46 : 0.30;
    if (mode === 'rival') return side === 'you' ? 0.40 : 0.34;
    return 0.36;
  }
  // Goal pocket depth — goals extend OUTWARD beyond the playfield. Puck must
  // travel into the pocket to score; corner posts deflect near-misses.
  const GOAL_DEPTH = 36;
  const POST_R = 5;

  function collidePost(post: Vec, prevX: number, prevY: number): void {
    const minDist = ball.r + POST_R;
    let dx = ball.p.x - post.x;
    let dy = ball.p.y - post.y;
    let dist = Math.hypot(dx, dy);
    // Continuous test: solve quadratic for the earliest t∈[0,1] along the
    // ball's substep trajectory (prev → cur) where distance to post = minDist.
    // Rewind ball to that contact and resolve from there. Fixes both the
    // "fast diagonal slips into goal" and "stuck/teleport on corner" bugs.
    if (dist >= minDist) {
      const sx = ball.p.x - prevX;
      const sy = ball.p.y - prevY;
      const lenSq = sx * sx + sy * sy;
      if (lenSq < 0.0001) return;
      const rx = prevX - post.x, ry = prevY - post.y;
      const aQ = lenSq;
      const bQ = 2 * (rx * sx + ry * sy);
      const cQ = rx * rx + ry * ry - minDist * minDist;
      const disc = bQ * bQ - 4 * aQ * cQ;
      if (disc < 0) return;
      const tImpact = (-bQ - Math.sqrt(disc)) / (2 * aQ);
      if (tImpact > 1 || tImpact < -0.0001) return;
      const tHit = Math.max(0, tImpact);
      const ix = prevX + sx * tHit;
      const iy = prevY + sy * tHit;
      // Push ball OUT of post to exact surface contact along contact normal.
      const idx = ix - post.x;
      const idy = iy - post.y;
      const idLen = Math.hypot(idx, idy);
      const ndx = idLen > 0.0001 ? idx / idLen : 1;
      const ndy = idLen > 0.0001 ? idy / idLen : 0;
      ball.p.x = post.x + ndx * minDist;
      ball.p.y = post.y + ndy * minDist;
      dx = ball.p.x - post.x; dy = ball.p.y - post.y; dist = minDist;
    }
    let nx: number, ny: number;
    if (dist < 0.0001) {
      const a = Math.random() * Math.PI * 2;
      nx = Math.cos(a); ny = Math.sin(a);
    } else { nx = dx / dist; ny = dy / dist; }
    ball.p.x = post.x + nx * minDist;
    ball.p.y = post.y + ny * minDist;
    const vn = ball.v.x * nx + ball.v.y * ny;
    if (vn < 0) {
      ball.v.x -= 2 * vn * nx;
      ball.v.y -= 2 * vn * ny;
      ball.v.x *= 0.88;
      ball.v.y *= 0.88;
    }
    if (!prefersReducedMotion()) spawnSparks(ball.p.x, ball.p.y, '#fbbf24', 4);
  }

  function collide(b: Ball, paddle: Paddle, who: 'you' | 'cpu'): void {
    const playerBonus = who === 'you' ? (mode === 'chill' ? 7 : mode === 'rival' ? 4 : 0) : 0;
    const effR = paddle.r + playerBonus;
    const minDist = b.r + effR;
    const dx = b.p.x - paddle.p.x;
    const dy = b.p.y - paddle.p.y;
    let dist = Math.hypot(dx, dy);

    // Swept-circle test: paddle moves once per frame while the ball substeps,
    // so a fast paddle can tunnel past the puck and the next overlap check
    // pushes the ball *deeper* into the paddle. Use the closest point on the
    // paddle's motion segment to detect mid-frame contact.
    const pdx = paddle.p.x - paddle.prev.x;
    const pdy = paddle.p.y - paddle.prev.y;
    const segLenSq = pdx * pdx + pdy * pdy;
    let sweptDist = dist;
    let sweptDx = dx, sweptDy = dy;
    if (segLenSq > 0.0001) {
      const t = clampNum(((b.p.x - paddle.prev.x) * pdx + (b.p.y - paddle.prev.y) * pdy) / segLenSq, 0, 1);
      const cpx = paddle.prev.x + pdx * t;
      const cpy = paddle.prev.y + pdy * t;
      const sdx = b.p.x - cpx;
      const sdy = b.p.y - cpy;
      const sd = Math.hypot(sdx, sdy);
      if (sd < sweptDist) { sweptDist = sd; sweptDx = sdx; sweptDy = sdy; }
    }
    const tunneling = dist >= minDist && sweptDist < minDist;
    if (!tunneling && dist >= minDist) return;

    // Determine contact normal (paddle → ball direction).
    let nx: number, ny: number;
    if (tunneling) {
      // Solve earliest impact time t∈[0,1] along paddle sweep so the normal
      // reflects the side ball came from (no teleport across paddle).
      const p0x = paddle.prev.x, p0y = paddle.prev.y;
      const rx = p0x - b.p.x, ry = p0y - b.p.y;
      const aQ = segLenSq;
      const bQ = 2 * (rx * pdx + ry * pdy);
      const cQ = rx * rx + ry * ry - minDist * minDist;
      const disc = bQ * bQ - 4 * aQ * cQ;
      let impactPx = paddle.p.x, impactPy = paddle.p.y;
      if (aQ > 0.0001 && disc >= 0) {
        const tImp = clampNum((-bQ - Math.sqrt(disc)) / (2 * aQ), 0, 1);
        impactPx = p0x + pdx * tImp;
        impactPy = p0y + pdy * tImp;
      }
      const idx = b.p.x - impactPx;
      const idy = b.p.y - impactPy;
      const idLen = Math.hypot(idx, idy);
      if (idLen > 0.0001) { nx = idx / idLen; ny = idy / idLen; }
      else if (sweptDist > 0.0001) { nx = sweptDx / sweptDist; ny = sweptDy / sweptDist; }
      else {
        const a = Math.random() * Math.PI * 2;
        nx = Math.cos(a); ny = Math.sin(a);
      }
    } else {
      if (dist < 0.0001) {
        const a = Math.random() * Math.PI * 2;
        nx = Math.cos(a); ny = Math.sin(a);
      } else { nx = dx / dist; ny = dy / dist; }
    }

    // Resolution: push BALL out first along the contact normal. Then if the
    // ball gets pinned by a playfield wall (can't fully separate), push the
    // PADDLE back by whatever overlap remains. Result: paddle freely shoves
    // the puck when there is room, only gets blocked when the puck has
    // nowhere to go. No more "paddle stuck on puck" feel.
    let newBallX = paddle.p.x + nx * minDist;
    let newBallY = paddle.p.y + ny * minDist;
    newBallX = clampNum(newBallX, b.r, W - b.r);
    newBallY = clampNum(newBallY, b.r, H - b.r);
    b.p.x = newBallX;
    b.p.y = newBallY;
    const fdx = b.p.x - paddle.p.x;
    const fdy = b.p.y - paddle.p.y;
    const fdist = Math.hypot(fdx, fdy);
    let ballPinned = false;
    if (fdist < minDist - 0.001) {
      const remOverlap = minDist - fdist;
      const fnx = fdist > 0.0001 ? fdx / fdist : nx;
      const fny = fdist > 0.0001 ? fdy / fdist : ny;
      paddle.p.x -= fnx * remOverlap;
      paddle.p.y -= fny * remOverlap;
      paddle.p = clampPos(paddle.p, who === 'you');
      // Re-evaluate normal/dist after paddle pushback for clean impulse calc.
      const r2dx = b.p.x - paddle.p.x;
      const r2dy = b.p.y - paddle.p.y;
      const r2 = Math.hypot(r2dx, r2dy);
      if (r2 > 0.0001) { nx = r2dx / r2; ny = r2dy / r2; }
      dist = Math.max(r2, minDist);
      ballPinned = true;
    } else {
      dist = fdist;
    }

    const vn_b = b.v.x * nx + b.v.y * ny;
    // Clamp paddle velocity used for impulse — fast finger swipes were
    // launching the puck cartoonishly. Cap effective paddle speed at a sane
    // arcade value.
    const paddleSpeed = Math.hypot(paddle.v.x, paddle.v.y);
    const maxPaddleSp = 1200;
    const pvScale = paddleSpeed > maxPaddleSp ? maxPaddleSp / paddleSpeed : 1;
    const pvx = paddle.v.x * pvScale;
    const pvy = paddle.v.y * pvScale;
    const vn_p = pvx * nx + pvy * ny;
    const approachingSpeed = vn_p - vn_b;
    if (approachingSpeed > 0) {
      // Lower restitution + neutral impulse scale. Gentle taps now produce
      // gentle hits; only fast strokes produce slapshots.
      const e = 0.7;
      const impulseScale = who === 'you' ? 1.0 : 1.0;
      const new_vn_b = ((1 + e) * vn_p - e * vn_b) * impulseScale;
      b.v.x += (new_vn_b - vn_b) * nx;
      b.v.y += (new_vn_b - vn_b) * ny;
    } else if (vn_b < 0) {
      // Already overlapping but ball moving back into paddle (post-tunnel
      // resolve, or paddle decelerating into ball). Flip normal velocity so
      // ball separates instead of sticking and re-clipping next frame.
      const new_vn_b = Math.max(-vn_b * 0.6, 80);
      b.v.x += (new_vn_b - vn_b) * nx;
      b.v.y += (new_vn_b - vn_b) * ny;
    }
    // Tangential transfer — light kinetic friction.
    const tx = -ny, ty = nx;
    const vt_b = b.v.x * tx + b.v.y * ty;
    const vt_p = pvx * tx + pvy * ty;
    const new_vt_b = vt_b + (vt_p - vt_b) * 0.14;
    b.v.x += (new_vt_b - vt_b) * tx;
    b.v.y += (new_vt_b - vt_b) * ty;

    // Wall-pinned puck slide-out: when ball can't separate (corner/wall pin)
    // and paddle keeps pressing, inject a tangential slide velocity toward
    // field center so the puck escapes along the wall instead of sticking.
    if (ballPinned) {
      const toCenterX = W / 2 - b.p.x;
      const toCenterY = H / 2 - b.p.y;
      const sign = (toCenterX * tx + toCenterY * ty) >= 0 ? 1 : -1;
      const slideSp = 260;
      const curTan = b.v.x * tx + b.v.y * ty;
      const desiredTan = sign * slideSp;
      if (Math.abs(curTan) < Math.abs(desiredTan) || Math.sign(curTan) !== sign) {
        const dv = desiredTan - curTan;
        b.v.x += dv * tx;
        b.v.y += dv * ty;
      }
    }

    // Player hit forgiveness — small forward impulse on casual taps so the
    // puck always moves away meaningfully, but nothing close to a slapshot.
    if (who === 'you' && b.v.y > -160) {
      b.v.y = Math.min(b.v.y, -160);
    }

    // Cap final puck speed to a reasonable arcade ceiling.
    const sp = Math.hypot(b.v.x, b.v.y);
    const maxSp = 1300;
    if (sp > maxSp) { b.v.x *= maxSp / sp; b.v.y *= maxSp / sp; }

    // Rally counts only when the puck changes hands. Same paddle staying in
    // contact (slow push, corner trap, multi-frame overlap) does not inflate
    // the rally. Wall touches reset ownership, so a paddle → wall → paddle
    // exchange still increments.
    const ownershipChanged = lastTouch !== who;
    // Rally only increments when ownership changed AND the puck actually
    // crossed the halfway line since the previous hit. Same-side rapid
    // contacts or grazing edge hits no longer inflate the count.
    const rallyCounts = ownershipChanged && puckCrossedHalfwaySinceLastHit;
    if (rallyCounts) {
      rallyHits += 1;
      rallyMs = 0;
      if (rallyHits > dbg.maxRally) dbg.maxRally = rallyHits;
      setText('[data-role="rally"]', rallyHits >= 3 ? `Rally <b>x${rallyHits}</b>` : '');
      if (rallyHits >= nextRallyMilestone) {
        pop(`RALLY x${rallyHits}`, 'good');
        nextRallyMilestone += 5;
      }
    }
    if (ownershipChanged) puckCrossedHalfwaySinceLastHit = false;
    if (who === 'you') {
      if (ownershipChanged) {
        dbg.playerHits += 1;
        if (sp > 560) { pop('SLAPSHOT', 'great'); haptic('tap'); }
        else haptic('tap');
        if (ball.p.y > H * 0.65 && ball.v.y < -200) cpuGoalThreatMs = 0;
        wallBouncesSinceYouHit = 0;
      }
      if (!prefersReducedMotion()) spawnSparks(b.p.x, b.p.y, '#5eead4', 9);
    } else {
      if (ownershipChanged) {
        dbg.cpuHits += 1;
        cpuRecoverMs = Math.max(cpuRecoverMs, getEffectiveProfile().recoveryMs);
        if (ball.v.y > 320) cpuGoalThreatMs = 600;
      }
      if (!prefersReducedMotion()) spawnSparks(b.p.x, b.p.y, '#fbbf24', 7);
    }
    lastTouch = who;
  }

  function scoreGoal(side: 'you' | 'cpu'): void {
    if (side === 'you') {
      scoreYou++; persisted.goalsFor++; dbg.playerGoals++;
      if (dbg.firstPlayerGoalMs === null) dbg.firstPlayerGoalMs = dbg.matchMs;
      const bankShot = wallBouncesSinceYouHit > 0 && lastTouch === 'wall';
      const comeback = scoreCpu - (scoreYou - 1) >= 2; // before-increment lead >=2
      if (bankShot) pop('BANK SHOT', 'great');
      else if (comeback) pop('COMEBACK', 'great');
      else pop('YOU SCORED', 'good');
      haptic('goal');
      goalFlash = { side: 'you', t: 1 };
    } else {
      scoreCpu++; persisted.goalsAgainst++; dbg.cpuGoals++;
      pop('Reset.', 'warn');
      haptic('lose');
      goalFlash = { side: 'cpu', t: 1 };
    }
    youNum.textContent = String(scoreYou);
    cpuNum.textContent = String(scoreCpu);
    bumpScore(side);
    void minigameState.put(MODULE_ID, persisted);
    setText('[data-role="rally"]', '');

    if (scoreYou >= WIN_SCORE || scoreCpu >= WIN_SCORE) {
      phase = 'ended';
      const youWin = scoreYou >= WIN_SCORE;
      if (youWin) {
        persisted.matchesWon++;
        persisted.currentMatchStreak++;
        persisted.longestMatchStreak = Math.max(persisted.longestMatchStreak, persisted.currentMatchStreak);
      } else {
        persisted.matchesLost++;
        persisted.currentMatchStreak = 0;
      }
      void minigameState.put(MODULE_ID, persisted);
      showEnd(youWin);
      return;
    }
    phase = 'goal';
    // CPU just scored → favor the player on the next serve.
    const serveTo: 'you' | 'cpu' = side === 'you' ? 'cpu' : 'you';
    later(() => reset(serveTo), 450);
  }

  function bumpScore(side: 'you' | 'cpu'): void {
    const node = side === 'you' ? youNum : cpuNum;
    node.classList.remove('bumped', 'you-flash', 'cpu-flash');
    void node.offsetWidth;
    node.classList.add('bumped', side === 'you' ? 'you-flash' : 'cpu-flash');
    later(() => node.classList.remove('bumped', 'you-flash', 'cpu-flash'), 700);
    if (side === 'you' && !prefersReducedMotion()) confetti();
  }

  function confetti(): void {
    const wrap2 = el('div', { class: 'ah-confetti' });
    const colors = ['#34d399', '#5eead4', '#67e8f9', '#fbbf24'];
    for (let i = 0; i < 22; i++) {
      const s = document.createElement('span');
      s.style.left = `${Math.random() * 100}%`;
      s.style.background = colors[i % colors.length] ?? '#34d399';
      s.style.animationDelay = `${Math.random() * 0.3}s`;
      s.style.transform = `rotate(${Math.random() * 360}deg)`;
      wrap2.appendChild(s);
    }
    wrap.appendChild(wrap2);
    later(() => wrap2.remove(), 1800);
  }

  function pop(text: string, kind: 'good' | 'great' | 'warn'): void {
    const node = el('div', { class: `ah-pop ${kind}` }, text);
    popLayer.appendChild(node);
    later(() => node.remove(), 950);
  }

  function showEnd(youWin: boolean): void {
    if (endNode) endNode.remove();
    const title = youWin ? 'You beat the wave.' : 'Still counted. You stayed with it.';
    const subtitle = youWin
      ? `${scoreYou}–${scoreCpu}. Want another?`
      : `${scoreCpu}–${scoreYou}. The craving moved on — that's the win.`;
    const stat = (lbl: string, val: string): HTMLElement =>
      el('div', {}, lbl, el('b', {}, val));
    endNode = el('div', { class: 'ah-end' },
      el('div', { class: 'panel' },
        el('h3', {}, title),
        el('p', {}, subtitle),
        el('div', { class: 'stats' },
          stat('Rally peak', String(dbg.maxRally)),
          stat('Hits', String(dbg.playerHits)),
          stat('Saves', String(dbg.greatSaves)),
        ),
        el('div', { class: 'stack' },
          el('button', { class: 'btn primary block', onclick: () => {
            endNode?.remove(); endNode = null;
            scoreYou = scoreCpu = 0;
            youNum.textContent = '0'; cpuNum.textContent = '0';
            // Reset debug stats for next match.
            dbg.matchMs = 0; dbg.firstPlayerGoalMs = null;
            dbg.playerHits = dbg.cpuHits = 0;
            dbg.playerGoals = dbg.cpuGoals = 0;
            dbg.cpuWhiffs = dbg.greatSaves = 0;
            dbg.maxRally = 0;
            reset();
          } }, 'Run it back'),
          el('button', { class: 'btn ghost block', onclick: () => doClose() }, 'Back to plan'),
        ),
      ),
    );
    wrap.appendChild(endNode);
  }

  function setText(sel: string, value: string): void {
    const node = stage.querySelector(sel) as HTMLElement | null;
    if (node) node.innerHTML = value;
  }

  // ---- Particles ----
  function spawnSparks(x: number, y: number, color: string, count = 8): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(80, 260);
      particles.push({
        p: { x, y },
        v: { x: Math.cos(a) * sp, y: Math.sin(a) * sp },
        life: rand(0.25, 0.55), maxLife: 0.55,
        size: rand(1.5, 3),
        color,
        kind: 'spark',
      });
    }
  }
  function spawnTrail(x: number, y: number): void {
    particles.push({
      p: { x, y },
      v: { x: 0, y: 0 },
      life: 0.32, maxLife: 0.32,
      size: ball.r * 0.5,
      color: '#fb7185',
      kind: 'trail',
    });
  }
  function updateParticles(dt: number): void {
    for (const p of particles) {
      p.life -= dt;
      p.p.x += p.v.x * dt;
      p.p.y += p.v.y * dt;
      if (p.kind === 'spark') {
        p.v.x *= Math.pow(0.92, dt * 60);
        p.v.y *= Math.pow(0.92, dt * 60);
      }
    }
    for (let i = particles.length - 1; i >= 0; i--) {
      if (particles[i]!.life <= 0) particles.splice(i, 1);
    }
  }
  function drawParticles(c: CanvasRenderingContext2D): void {
    for (const p of particles) {
      const a = clampNum(p.life / p.maxLife, 0, 1);
      c.globalAlpha = a;
      c.fillStyle = p.color;
      c.beginPath();
      c.arc(p.p.x, p.p.y, p.size, 0, Math.PI * 2);
      c.fill();
    }
    c.globalAlpha = 1;
  }

  // ---- Tick / render ----
  function tickPhysics(dt: number): void {
    nowMs += dt * 1000;
    dbg.matchMs += dt * 1000;
    rememberBall();
    updatePlayer(dt);
    updateCpu(dt);
    updateBall(dt);
    updateParticles(dt);
  }

  function render(): void {
    const c = ctx2d!;
    // Clear the entire canvas including pocket headroom above and below.
    c.clearRect(0, -GOAL_DEPTH, W, H + GOAL_DEPTH * 2);

    // Neon arcade table.
    const grad = c.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, '#0e2240');
    grad.addColorStop(0.5, '#0a1830');
    grad.addColorStop(1, '#0e2238');
    c.fillStyle = grad;
    c.fillRect(0, 0, W, H);

    // CPU side glow (amber, top).
    const cpuGlow = c.createRadialGradient(W / 2, 0, 20, W / 2, 0, H * 0.55);
    cpuGlow.addColorStop(0, 'rgba(251,191,36,0.18)');
    cpuGlow.addColorStop(1, 'rgba(251,191,36,0)');
    c.fillStyle = cpuGlow;
    c.fillRect(0, 0, W, H);
    // Player side glow (cyan, bottom).
    const youGlow = c.createRadialGradient(W / 2, H, 20, W / 2, H, H * 0.55);
    youGlow.addColorStop(0, 'rgba(94,234,212,0.22)');
    youGlow.addColorStop(1, 'rgba(94,234,212,0)');
    c.fillStyle = youGlow;
    c.fillRect(0, 0, W, H);

    // Centerline pulse.
    const pulse = (Math.sin(nowMs * 0.003) * 0.5 + 0.5) * 0.4 + 0.3;
    c.strokeStyle = `rgba(94,234,212,${pulse})`;
    c.lineWidth = 2;
    c.setLineDash([10, 8]);
    c.beginPath(); c.moveTo(0, H / 2); c.lineTo(W, H / 2); c.stroke();
    c.setLineDash([]);

    // Center circle.
    c.strokeStyle = 'rgba(94,234,212,0.20)';
    c.lineWidth = 2;
    c.beginPath(); c.arc(W / 2, H / 2, Math.min(W, H) * 0.10, 0, Math.PI * 2); c.stroke();

    // Goal pockets — rectangles extending outward from each end. The puck
    // must travel into the pocket to score; corner posts deflect near-misses.
    const goalLeftT = W / 2 - (W * goalWidthFor('you')) / 2;
    const goalRightT = W / 2 + (W * goalWidthFor('you')) / 2;
    const goalLeftB = W / 2 - (W * goalWidthFor('cpu')) / 2;
    const goalRightB = W / 2 + (W * goalWidthFor('cpu')) / 2;

    // Top pocket (CPU's net — player scores here).
    c.fillStyle = 'rgba(94,234,212,0.10)';
    c.fillRect(goalLeftT, -GOAL_DEPTH, goalRightT - goalLeftT, GOAL_DEPTH);
    c.strokeStyle = 'rgba(94,234,212,0.55)';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(goalLeftT, 0);
    c.lineTo(goalLeftT, -GOAL_DEPTH);
    c.lineTo(goalRightT, -GOAL_DEPTH);
    c.lineTo(goalRightT, 0);
    c.stroke();
    // Goal line tint.
    c.fillStyle = 'rgba(94,234,212,0.22)';
    c.fillRect(goalLeftT, 0, goalRightT - goalLeftT, 3);

    // Bottom pocket (player's net — CPU scores here).
    const danger = dangerZoneActive;
    c.fillStyle = danger ? 'rgba(248,113,113,0.16)' : 'rgba(251,191,36,0.08)';
    c.fillRect(goalLeftB, H, goalRightB - goalLeftB, GOAL_DEPTH);
    c.strokeStyle = danger ? 'rgba(248,113,113,0.7)' : 'rgba(251,191,36,0.5)';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(goalLeftB, H);
    c.lineTo(goalLeftB, H + GOAL_DEPTH);
    c.lineTo(goalRightB, H + GOAL_DEPTH);
    c.lineTo(goalRightB, H);
    c.stroke();
    c.fillStyle = danger ? 'rgba(248,113,113,0.28)' : 'rgba(251,191,36,0.20)';
    c.fillRect(goalLeftB, H - 3, goalRightB - goalLeftB, 3);

    // Posts at mouth corners — visible bumpers.
    const drawPost = (x: number, y: number, color: string): void => {
      c.beginPath();
      c.arc(x, y, POST_R, 0, Math.PI * 2);
      c.fillStyle = color;
      c.fill();
      c.lineWidth = 1;
      c.strokeStyle = 'rgba(255,255,255,0.5)';
      c.stroke();
    };
    drawPost(goalLeftT, 0, '#5eead4');
    drawPost(goalRightT, 0, '#5eead4');
    drawPost(goalLeftB, H, danger ? '#f87171' : '#fbbf24');
    drawPost(goalRightB, H, danger ? '#f87171' : '#fbbf24');

    // Rails (inner border glow).
    c.strokeStyle = 'rgba(255,255,255,0.06)';
    c.lineWidth = 1;
    c.strokeRect(1, 1, W - 2, H - 2);

    // Particles below paddles.
    drawParticles(c);

    // Goal flash.
    if (goalFlash) {
      goalFlash.t -= 1 / 60;
      const alpha = Math.max(0, goalFlash.t);
      c.fillStyle = goalFlash.side === 'you'
        ? `rgba(94,234,212,${alpha * 0.32})`
        : `rgba(248,113,113,${alpha * 0.25})`;
      c.fillRect(0, 0, W, H);
      if (goalFlash.t <= 0) goalFlash = null;
    }

    drawPaddle(c, cpu, '#fbbf24');
    drawPaddle(c, player, '#5eead4');
    drawBall(c, ball);

    // Finger ghost + tether — only when recently moved.
    if (fingerPos && nowMs - fingerSeenMs < 500) {
      const a = 1 - (nowMs - fingerSeenMs) / 500;
      c.globalAlpha = a * 0.5;
      c.strokeStyle = 'rgba(255,255,255,0.6)';
      c.lineWidth = 1;
      c.setLineDash([3, 4]);
      c.beginPath();
      c.moveTo(fingerPos.x, fingerPos.y);
      c.lineTo(player.p.x, player.p.y);
      c.stroke();
      c.setLineDash([]);
      c.beginPath();
      c.arc(fingerPos.x, fingerPos.y, 14, 0, Math.PI * 2);
      c.stroke();
      c.globalAlpha = 1;
    }
  }

  function drawPaddle(c: CanvasRenderingContext2D, paddle: Paddle, color: string): void {
    c.beginPath();
    c.arc(paddle.p.x, paddle.p.y + 6, paddle.r, 0, Math.PI * 2);
    c.fillStyle = 'rgba(0,0,0,0.45)';
    c.fill();
    const grad = c.createRadialGradient(paddle.p.x - paddle.r * 0.3, paddle.p.y - paddle.r * 0.3, paddle.r * 0.2, paddle.p.x, paddle.p.y, paddle.r);
    grad.addColorStop(0, lighten(color, 0.35));
    grad.addColorStop(1, color);
    c.beginPath();
    c.arc(paddle.p.x, paddle.p.y, paddle.r, 0, Math.PI * 2);
    c.fillStyle = grad; c.fill();
    // Glow ring.
    c.beginPath();
    c.arc(paddle.p.x, paddle.p.y, paddle.r + 1, 0, Math.PI * 2);
    c.strokeStyle = color + 'cc';
    c.lineWidth = 1;
    c.stroke();
    // Inner divot.
    c.beginPath();
    c.arc(paddle.p.x, paddle.p.y, paddle.r * 0.5, 0, Math.PI * 2);
    c.fillStyle = 'rgba(0,0,0,0.4)';
    c.fill();
  }

  function drawBall(c: CanvasRenderingContext2D, b: Ball): void {
    // Top-down cylindrical hockey puck: flat solid red face, dark cylindrical
    // side visible only as a sliver at the bottom (depth illusion), soft
    // contact shadow on the table. No spherical sheen.
    const x = b.p.x, y = b.p.y, r = b.r;

    // Contact shadow on the table.
    c.beginPath();
    c.ellipse(x, y + r * 0.55, r * 1.05, r * 0.4, 0, 0, Math.PI * 2);
    c.fillStyle = 'rgba(0,0,0,0.45)';
    c.fill();

    // Side wall (cylinder thickness) — disc offset slightly downward so the
    // bottom edge of the top face reveals the dark side.
    c.beginPath();
    c.arc(x, y + r * 0.22, r, 0, Math.PI * 2);
    c.fillStyle = '#2a0303';
    c.fill();

    // Top face — solid red with a very subtle radial darken at edge. Reads
    // flat, not spherical.
    const grad = c.createRadialGradient(x, y, r * 0.6, x, y, r);
    grad.addColorStop(0, '#dc2626');
    grad.addColorStop(0.8, '#b91c1c');
    grad.addColorStop(1, '#7f1d1d');
    c.beginPath();
    c.arc(x, y, r, 0, Math.PI * 2);
    c.fillStyle = grad; c.fill();

    // Edge line of the top face — sharp boundary so it reads as a real disc.
    c.beginPath();
    c.arc(x, y, r, 0, Math.PI * 2);
    c.strokeStyle = 'rgba(0,0,0,0.45)';
    c.lineWidth = 1;
    c.stroke();

    // Inner concentric ring detail.
    c.beginPath();
    c.arc(x, y, r * 0.62, 0, Math.PI * 2);
    c.strokeStyle = 'rgba(0,0,0,0.25)';
    c.lineWidth = 1;
    c.stroke();
  }

  function lighten(hex: string, amt: number): string {
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m || !m[1]) return hex;
    const n = parseInt(m[1], 16);
    const r = Math.min(255, Math.round(((n >> 16) & 0xff) + 255 * amt));
    const g = Math.min(255, Math.round(((n >> 8) & 0xff) + 255 * amt));
    const b = Math.min(255, Math.round((n & 0xff) + 255 * amt));
    return `rgb(${r},${g},${b})`;
  }

  function frame(now: number): void {
    if (!alive) return;
    const dt = Math.min(1 / 30, (now - lastFrame) / 1000);
    lastFrame = now;
    tickPhysics(dt);
    render();
    rafId = requestAnimationFrame(frame);
  }

  function doClose(): void {
    alive = false;
    cancelAnimationFrame(rafId);
    document.body.classList.remove('ah-active');
    for (const id of pendingTimeouts) clearTimeout(id);
    pendingTimeouts.clear();
    for (const fn of cleanupFns) fn();
    void minigameState.put(MODULE_ID, persisted);
    resolve({ kind: 'completed', details: { you: scoreYou, cpu: scoreCpu, won: scoreYou > scoreCpu } });
  }

  // ---- Init ----
  const ro = new ResizeObserver(() => { resize(); render(); });
  ro.observe(wrap);
  addCleanup(() => ro.disconnect());
  resize();
  reset();
  rafId = requestAnimationFrame(frame);
}
